import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, Map, Navigation, Users } from "lucide-react";
import { useState } from "react";

// Define the filter options types
type IgnitionStatus = "all" | "on" | "off";
type SpeedRange =
  | "all"
  | "above10"
  | "above20"
  | "above30"
  | "above40"
  | "above50"
  | "above60"
  | "above80"
  | "above100"
  | "above110"
  | "above120"
  | "above130"
  | "above140"
  | "above150"
  | "above160";

// Props interface
interface PositionsReportFiltersProps {
  onFilterChange: (filters: {
    ignitionStatus: IgnitionStatus;
    speedRange: SpeedRange;
  }) => void;
  onMapButtonClick?: () => void;
  dayData?: {
    first_movement?: string | null;
    last_movement?: string | null;
    totals?: {
      distance?: number;
    };
    drivers_list?: string[];
  } | null;
}

const PositionsReportFilters = ({
  onFilterChange,
  onMapButtonClick,
  dayData,
}: PositionsReportFiltersProps) => {
  const [ignitionStatus, setIgnitionStatus] = useState<IgnitionStatus>("all");
  const [speedRange, setSpeedRange] = useState<SpeedRange>("all");

  const speedRangeOptions = [
    { value: "all", label: "Todas as velocidades" },
    { value: "above10", label: "Acima de 10km/h" },
    { value: "above20", label: "Acima de 20km/h" },
    { value: "above30", label: "Acima de 30km/h" },
    { value: "above40", label: "Acima de 40km/h" },
    { value: "above50", label: "Acima de 50km/h" },
    { value: "above60", label: "Acima de 60km/h" },
    { value: "above80", label: "Acima de 80km/h" },
    { value: "above100", label: "Acima de 100km/h" },
    { value: "above110", label: "Acima de 110km/h" },
    { value: "above120", label: "Acima de 120km/h" },
    { value: "above130", label: "Acima de 130km/h" },
    { value: "above140", label: "Acima de 140km/h" },
    { value: "above150", label: "Acima de 150km/h" },
    { value: "above160", label: "Acima de 160km/h" },
  ];

  // Handle filter changes
  const handleIgnitionChange = (value: IgnitionStatus) => {
    setIgnitionStatus(value);
    onFilterChange({
      ignitionStatus: value,
      speedRange,
    });
  };

  const handleSpeedRangeChange = (value: SpeedRange) => {
    setSpeedRange(value);
    onFilterChange({
      ignitionStatus,
      speedRange: value,
    });
  };

  const formatTime = (dateTime: string | null | undefined) => {
    if (!dateTime) return "Sem registro";
    try {
      const date = new Date(dateTime);
      return date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Sem registro";
    }
  };

  const formatDistance = (distanceInMeters: number | undefined) => {
    if (!distanceInMeters || distanceInMeters === 0) return "0 km";
    const distanceInKm = distanceInMeters / 1000;
    return `${distanceInKm.toFixed(2)} km`;
  };

  const firstMovement = dayData?.first_movement;
  const lastMovement = dayData?.last_movement;
  const distance = dayData?.totals?.distance;
  const drivers = dayData?.drivers_list ?? [];

  return (
    <Card className="mb-4">
      <CardContent className="pt-4 space-y-4">
        <div className="flex flex-wrap gap-4 pb-4 border-b">
          <div className="flex items-center">
            <Button
              onClick={onMapButtonClick}
              variant="default"
              className="gap-2 dark:border dark:border-white"
            >
              <Map className="w-4 h-4" />
              Ver no mapa
            </Button>
          </div>
          <div className="flex flex-wrap gap-6 items-center flex-1">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                <Clock className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Primeira Movimentação
                </p>
                <p className="text-sm font-medium">
                  {formatTime(firstMovement)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                <Clock className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Última Movimentação
                </p>
                <p className="text-sm font-medium">
                  {formatTime(lastMovement)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                <Navigation className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Distância</p>
                <p className="text-sm font-medium">
                  {formatDistance(distance)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Motoristas</p>
                <p className="text-sm font-medium">
                  {drivers.length > 0 ? drivers.join(", ") : "Sem motorista"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          {/* Ignition Status Filter */}
          <div className="flex-1 min-w-[200px]">
            <div className="space-y-2">
              <Label htmlFor="ignition-status">Status da ignição</Label>
              <Select
                value={ignitionStatus}
                onValueChange={(value) =>
                  handleIgnitionChange(value as IgnitionStatus)
                }
              >
                <SelectTrigger id="ignition-status">
                  <SelectValue placeholder="Status da ignição" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="on">Ligada</SelectItem>
                  <SelectItem value="off">Desligada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Speed Range Filter */}
          <div className="flex-1 min-w-[200px]">
            <div className="space-y-2">
              <Label htmlFor="speed-range">Faixa de velocidade</Label>
              <Select
                value={speedRange}
                onValueChange={(value) =>
                  handleSpeedRangeChange(value as SpeedRange)
                }
              >
                <SelectTrigger id="speed-range">
                  <SelectValue placeholder="Faixa de velocidade" />
                </SelectTrigger>
                <SelectContent>
                  {speedRangeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PositionsReportFilters;
